/**
 * Pointer input: drag to orbit, wheel to dolly.
 *
 * Deltas accumulate into fields as the browser delivers events, and are drained once per
 * frame by whoever consumes them. Camera movement therefore happens on the frame clock rather
 * than at the whim of pointer event coalescing, and the handler itself allocates nothing.
 *
 * Uses Pointer Events, so mouse, trackpad, pen and touch all work through one path — which is
 * also what the touch tier will need.
 *
 * `updateFrame(alpha)`: the *consumer* calls `drain*()` once per frame.
 */

export interface PointerControlsOptions {
  /** Radians of yaw per pixel dragged. */
  yawPerPixel?: number;
  /** Radians of pitch per pixel dragged. */
  pitchPerPixel?: number;
  /** Multiplicative dolly per wheel notch. */
  dollyPerNotch?: number;
}

export class PointerControls {
  private readonly element: HTMLElement;
  private readonly yawPerPixel: number;
  private readonly pitchPerPixel: number;
  private readonly dollyPerNotch: number;

  private pendingYaw = 0;
  private pendingPitch = 0;
  private pendingDolly = 1;
  private activePointerId = -1;
  private lastX = 0;
  private lastY = 0;
  private attached = false;

  constructor(element: HTMLElement, options: PointerControlsOptions = {}) {
    this.element = element;
    this.yawPerPixel = options.yawPerPixel ?? 0.005;
    this.pitchPerPixel = options.pitchPerPixel ?? 0.004;
    this.dollyPerNotch = options.dollyPerNotch ?? 1.12;
  }

  attach(): void {
    if (this.attached) return;
    this.element.addEventListener('pointerdown', this.onPointerDown);
    this.element.addEventListener('pointermove', this.onPointerMove);
    this.element.addEventListener('pointerup', this.onPointerUp);
    this.element.addEventListener('pointercancel', this.onPointerUp);
    this.element.addEventListener('wheel', this.onWheel, { passive: false });
    this.element.addEventListener('contextmenu', this.onContextMenu);
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    this.element.removeEventListener('pointerdown', this.onPointerDown);
    this.element.removeEventListener('pointermove', this.onPointerMove);
    this.element.removeEventListener('pointerup', this.onPointerUp);
    this.element.removeEventListener('pointercancel', this.onPointerUp);
    this.element.removeEventListener('wheel', this.onWheel);
    this.element.removeEventListener('contextmenu', this.onContextMenu);
    this.attached = false;
  }

  get isDragging(): boolean {
    return this.activePointerId !== -1;
  }

  /** Yaw accumulated since the last call [rad]. Resets the accumulator. */
  drainYaw(): number {
    const value = this.pendingYaw;
    this.pendingYaw = 0;
    return value;
  }

  /** Pitch accumulated since the last call [rad]. Resets the accumulator. */
  drainPitch(): number {
    const value = this.pendingPitch;
    this.pendingPitch = 0;
    return value;
  }

  /** Dolly factor accumulated since the last call (1 = no change). Resets the accumulator. */
  drainDolly(): number {
    const value = this.pendingDolly;
    this.pendingDolly = 1;
    return value;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.activePointerId !== -1) return;
    this.activePointerId = event.pointerId;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.element.setPointerCapture(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    // Drag right → the camera swings right around the aircraft.
    this.pendingYaw -= dx * this.yawPerPixel;
    this.pendingPitch -= dy * this.pitchPerPixel;
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    this.element.releasePointerCapture?.(event.pointerId);
    this.activePointerId = -1;
  };

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault(); // stop the page from scrolling under the canvas
    // deltaMode 0 is pixels (trackpads), 1 is lines (mouse wheels). Normalise to notches.
    const notches = event.deltaMode === 0 ? event.deltaY / 100 : event.deltaY / 3;
    this.pendingDolly *= Math.pow(this.dollyPerNotch, notches);
  };

  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault(); // right-drag should orbit, not open a menu
  };
}
